<template>
  <div class="grid grid-cols-1 col-gap-12 row-gap-11 items-center lg:grid-cols-2">
    <div class="lg:col-span-2 flex items-center" v-if="!edit_item">
      <div class="text-xl font-bold text-text leading-tight ">{{ $t('formTitle') }}</div>
    </div>
    <BaseSelect v-model="category"
                :options="type_list"
                track-by="id"
                label="name"
                :select-label="$t('categoryLabel')"
                group-values="categories"
                group-label="name"
                :group-select="false"
                :allow-empty="false"
                :searchable="false"
                :show-labels="false"
                @input="handleType"/>
    <BaseInput v-model.trim="$v.title.$model"
               :label="$t('nameLabel')"
               :class="{'--error':$v.title.$error }">
      <template v-slot:errors="">
        <div class="error" v-if="!$v.title.required">{{ $t('errors.required') }}</div>
        <div class="error" v-else-if="!$v.title.noSpecSymbols">{{ $t('errors.noSpecSymbols') }}</div>
      </template>
    </BaseInput>
    <div>
      <MoreSettings class="ml-3 px-2 relative z-10"
                    :class="more_settings && 'bg-block'"
                    @click="toggleMoreSettings(!more_settings)"
                    :active="more_settings"/>
    </div>
    <div v-if="more_settings"
         class="grid grid-cols-1 col-span-1 col-start-1 col-gap-12 row-gap-12 relative
                border rounded border-border
                p-10 -mt-14">
      <IconPicker
          :selected="icon"
          :icon-color="icon_color"
          :icon-bg="icon_bg"
          @change="handleIconSelect">
        {{ $t('pickIconLabel') }}
      </IconPicker>
      <IconColorPicker
          v-model="icon_bg"
          :hidden="isBankIconSelected"
          @change="handleIconColor"/>
      <BaseSelect
          v-model="involvement"
          :options="income_involvement"
          track-by="id"
          label="name"
          :select-label="$t('involvementLabel')"
          :searchable="false"
          :allow-empty="false"
          :show-labels="false"
          @input="handleInvolvement"/>
    </div>
    <div v-if="!edit_item" class="lg:col-span-2 grid grid-cols-2 items-center col-gap-12">
      <BaseButton theme="red"
                  class="lg:w-91"
                  @click="create"
                  :loading="submitted">
        {{ $t('createIncomeSource') }}
      </BaseButton>
    </div>
    <div v-else class="lg:col-span-2 flex flex-wrap items-center">
      <BaseButton @click="save"
                  class="w-78 lg:w-91 mr-8"
                  theme="gray"
                  :loading="submitted">
        {{ $t('saveChanges') }}
      </BaseButton>
      <BaseButton @click="$emit('cancelEdit')"
                  :loading="submitted"
                  class="w-50 lg:w-68 mr-20"
                  theme="white">
        {{ $t('cancel') }}
      </BaseButton>
      <ArchiveBtn v-if="edit_item && incomes.length !== 1"
                  class="ml-auto"
                  :disabled="edit_item.is_default"
                  :disabled-tooltip="$t('disabledRemoveTooltipIsSystem')"
                  :submitted="submitted"
                  @confirm="archive">
        <template v-slot:text>
          {{ $t('removeLabel') }}
        </template>
      </ArchiveBtn>
    </div>
  </div>
</template>

<script>
import {helpers, required} from 'vuelidate/lib/validators'
import {mapActions, mapGetters, mapState} from "vuex";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseButton from "@/components/UI/BaseButton";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import MoreSettings from "@/components/common/MoreSettings";
import IconPicker from '@/components/common/IconPicker/IconPicker.vue'
import IconColorPicker from '@/components/common/IconPicker/IconColorPicker.vue'
import {isBankIcon} from '@/components/icons/MKIcons/icons'
import {INCOME_ICON_BG, ICON_GLYPH, editingIconBg, iconColorPayload} from '@/components/icons/MKIcons/iconPalette'
import ArchiveBtn from '@/components/UI/ArchiveBtn.vue'

const noSpecSymbols = helpers.regex('noSpecSymbols', /^[^*|\/":<>[\]{}`\\()';@&!$]+$/)

export default {
  name: "IncomeSettingsForm",
  props: {
    edit_item: {
      type: Object,
      default: null
    }
  },
  components: {
    MoreSettings,
    BaseInput,
    BaseSelect,
    BaseButton,
    IconPicker,
    IconColorPicker,
    ArchiveBtn,
  },
  data() {
    return {
      more_settings: !!localStorage.getItem('settings') ? localStorage.getItem('settings') === '1' : false,
      submitted: false,

      icon: 'chart_pie',
      icon_bg: INCOME_ICON_BG,
      icon_color: ICON_GLYPH,
      title: '',
      category: null,
      involvement: null,
    }
  },
  validations: {
    title: {
      required,
      noSpecSymbols,
    }
  },
  computed: {
    ...mapGetters('cabinet/currencies', ['currenciesList']),
    ...mapState('space', ['current_space']),
    ...mapState('space/incomes', ['incomes']),
    ...mapGetters('cabinet/constants', ['getConstantList']),
    income_involvement() {
      return this.getConstantList('income_involvement')
    },
    income_categories() {
      return this.getConstantList('income_category')
    },
    income_category_group() {
      return this.getConstantList('income_category_group')
    },
    isBankIconSelected() {
      return isBankIcon(this.icon);
    },
    type_list: {
      get() {
        let groups = this.income_category_group,
            categories = this.income_categories,
            arr = [];
        arr = groups.map(i => ({...i, categories: categories.filter(j => j.extra.group_id == i.id)}))
        return arr;
      }
    }
  },
  methods: {
    ...mapActions('space/incomes', ['getIncomes', 'createIncomeSource', 'saveIncomeSource', 'deleteIncomeSource']),
    toggleMoreSettings(val) {
      localStorage.setItem('settings', val ? '1' : '0');
      this.more_settings = val;
    },
    handleType(val) {
      this.category = val;
    },
    handleIconSelect(val) {
      const wasBank = isBankIcon(this.icon);
      this.icon = val;
      if (isBankIcon(val)) {
        this.icon_bg = null;
        this.icon_color = null;
      } else if (wasBank) {
        this.icon_bg = INCOME_ICON_BG;
        this.icon_color = ICON_GLYPH;
      }
    },
    handleIconColor({ bg, color }) {
      this.icon_bg = bg;
      this.icon_color = color;
    },
    handleInvolvement(val) {
      this.involvement = val;
    },
    create() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          space_id: this.current_space.data.id,
          icon: this.icon,
          title: this.title,
          category_id: this.category.id,
          involvement_id: this.involvement.id,
          ...iconColorPayload(this.icon_bg, this.isBankIconSelected),
        };
        this.createIncomeSource(data).then(res => {
          if (res.body.success) {
            this.getIncomes();
            this.clearFields();
            this.$v.$reset();
            this.$toasted.success('Источник дохода добавлен')
          } else {
            this.$toasted.error('Произошла ошибка')
            console.log(res)
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        document.documentElement.scrollTop = 0;
        this.$toasted.error('Проверьте поля')
      }
    },
    save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let data = {
          id: this.edit_item.id,
          icon: this.icon,
          title: this.title,
          category_id: this.category.id,
          involvement_id: this.involvement.id,
          ...iconColorPayload(this.icon_bg, this.isBankIconSelected),
        };
        this.saveIncomeSource(data).then((res) => {
          if (res.body.success) {
            this.$emit('cancelEdit')
            this.getIncomes();
            this.clearFields();
            this.$v.$reset();
            this.$toasted.success('Источник дохода успешно изменен')
          } else {
            this.$toasted.error('Произошла ошибка')
            console.log(res)
          }
        }).finally(() => {
          this.submitted = false;
        });
      } else {
        this.$toasted.error('Проверьте поля')
      }
    },
    archive() {
      this.submitted = true;
      this.deleteIncomeSource(this.edit_item.id).then(res => {
        if (res.body.success) {
          this.$emit('cancelEdit');
          this.getIncomes();
          this.$toasted.info('Источник дохода удален')
        }
      }).finally(() => {
        this.submitted = false;
      });
    },
    setEditFields(item) {
      this.more_settings = true;
      this.icon = item.icon;
      this.icon_bg = editingIconBg(item.icon_bg, INCOME_ICON_BG, isBankIcon(item.icon));
      this.icon_color = this.icon_bg ? ICON_GLYPH : null;
      this.title = item.title;
      this.category = this.income_categories.filter(i => i.id == item.category_id)[0];
      this.involvement = this.income_involvement.filter(i => i.id == item.involvement_id)[0];
    },
    clearFields() {
      this.more_settings = !!localStorage.getItem('settings') ? localStorage.getItem('settings') === '1' : false;
      this.icon = 'chart_pie';
      this.icon_bg = INCOME_ICON_BG;
      this.icon_color = ICON_GLYPH;
      this.title = '';
      this.category = this.income_categories[0];
      this.involvement = this.income_involvement.filter(i => i.id == this.category.extra.involvement_id)[0];
      this.$v.$reset();
    },
  },
  watch: {
    edit_item: {
      immediate: true,
      handler(newVal) {
        if (!!newVal) {
          this.setEditFields(newVal);
        }
      }
    },
    category: {
      immediate: true,
      handler: function (newVal) {
        if (!!newVal && !this.edit_item) {
          this.involvement = this.income_involvement.filter(i => i.id == newVal.extra.involvement_id)[0];
        }
      }
    }
  },
  mounted() {
    if (!this.edit_item) {
      this.category = this.income_categories[0]
    }
  },
  i18n: {
    messages: {
      en: {
        formTitle: 'Add income source',
        categoryLabel: 'Income category',
        nameLabel: 'Income name',
        pickIconLabel: 'Pick an icon for income',
        involvementLabel: 'Involvement',
        createIncomeSource: 'Create income',
        saveChanges: 'Save changes',
        cancel: 'Cancel',
        removeLabel: 'Delete income source',
        disabledRemoveTooltipIsSystem: 'Unable to delete, is a system source.'
      },
      ru: {
        formTitle: 'Добавление источника дохода',
        categoryLabel: 'Категория дохода',
        nameLabel: 'Название источника',
        pickIconLabel: 'Выбрать иконку для источника',
        involvementLabel: 'Вовлеченность',
        createIncomeSource: 'Добавить источник',
        saveChanges: 'Сохранить изменения',
        cancel: 'Отмена',
        removeLabel: 'Удалить источник',
        disabledRemoveTooltipIsSystem: 'Невозможно удалить, является системным источником.'
      }
    }
  }
}
</script>
